<script lang="ts">
export default {
  name: 'FilterButtonV2',
};
</script>

<script setup lang="ts">
import type { Component } from 'vue';
import { ref, watch, nextTick, onUnmounted, computed } from 'vue';
import { Filter } from '@lucide/vue';
import { useAnchoredMenuPosition } from '@/composables/useAnchoredMenuPosition';

const props = withDefaults(
  defineProps<{
    title?: string;
    /** When true, trigger shows active styling (e.g. bold). */
    active?: boolean;
    dataTestActivator?: string;
    /** Align menu with start (left) of trigger. Default is end (right). */
    start?: boolean;
    /** Optional Lucide (or other) icon component; when omitted, Filter is used. */
    icon?: Component;
  }>(),
  { active: false, start: false },
);

const iconComponent = computed(() => props.icon ?? Filter);

const emit = defineEmits<{
  save: [payload?: unknown];
  cancel: [];
}>();

const menuOpen = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const popupRef = ref<HTMLElement | null>(null);
const { menuStyle: popupStyle, updateMenuPosition: updatePopupPosition } = useAnchoredMenuPosition(triggerRef, popupRef, props);

function close() {
  menuOpen.value = false;
}

function save(payload?: unknown) {
  menuOpen.value = false;
  emit('save', payload);
}

function cancel() {
  menuOpen.value = false;
  emit('cancel');
}

function handleClickOutside(e: MouseEvent) {
  const target = e.target as Node;
  if (triggerRef.value?.contains(target) || popupRef.value?.contains(target)) return;
  cancel();
}

let clickOutsideCleanup: (() => void) | null = null;

watch(menuOpen, (value) => {
  if (!value) {
    popupStyle.value = null;
    clickOutsideCleanup?.();
    clickOutsideCleanup = null;
    return;
  }
  nextTick(() => {
    updatePopupPosition();
    const onScroll = (e: Event) => {
      if (popupRef.value?.contains(e.target as Node)) return;
      cancel();
    };
    clickOutsideCleanup = () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('scroll', onScroll, true);
    };
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('scroll', onScroll, true);
  });
});

onUnmounted(() => {
  clickOutsideCleanup?.();
});

defineExpose({ close, save, cancel });
</script>

<template>
  <div class="relative inline-block">
    <label
      ref="triggerRef"
      tabindex="0"
      class="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-solid px-4 py-1.5 text-sm transition-colors select-none hover:bg-gray-100"
      :class="[active ? 'border-gray-600 font-semibold' : 'border-gray-400', 'hover:bg-gray-50 bg-white']"
      :data-testid="dataTestActivator ?? 'filter-button-activator'"
      @click="menuOpen = !menuOpen"
      @keydown.enter.space.prevent="menuOpen = !menuOpen">
      <component
        :is="iconComponent"
        class="h-3.5 w-3.5 shrink-0" />
      <span class="whitespace-nowrap">{{ title ?? 'Filter' }}</span>
    </label>

    <Teleport to="body">
      <div
        v-if="menuOpen"
        ref="popupRef"
        class="fixed z-9999 min-w-[280px] rounded-md border border-gray-200 bg-white shadow-lg"
        :style="popupStyle ?? undefined"
        @click.stop>
        <slot
          name="menu"
          :close="close"
          :save="save"
          :cancel="cancel" />
      </div>
    </Teleport>
  </div>
</template>
